import { CheckIcon, XMarkIcon } from '@heroicons/react/24/solid';
import classnames from 'classnames';
import React, { useCallback, useEffect, useRef } from 'react';
import { usePredictions } from '../../../contexts/PredictionsContext';
import { useCurrentDetectionId } from '../../../contexts/predictions.hooks';
import { Detection, Prediction } from '../../../types/prediction.type';
import { StatusInput } from './StatusInput';

type DetectionStatusProps = {
  prediction: Prediction;
  detection: Detection;
  index: number;
  isDisplayed?: boolean;
};

export function DetectionStatus({
  prediction,
  detection,
  index,
  isDisplayed,
}: DetectionStatusProps) {
  const [currentAnnotationId, setCurrentAnnotationId] = useCurrentDetectionId();

  const { onDetectionSelection } = usePredictions();

  const { status } = detection;

  const liRef = useRef<HTMLLIElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const isActive = detection.annotationId === currentAnnotationId;

  useEffect(() => {
    if (isActive && isDisplayed) {
      onDetectionSelection(prediction, detection);
      formRef.current?.focus();
    }
  }, [isActive, isDisplayed]);

  const onFocus = useCallback(() => {
    setCurrentAnnotationId(detection.annotationId);
    liRef.current?.scrollIntoView({
      block: 'center',
    });
  }, [detection.annotationId]);

  const onDoubleClick = useCallback(() => {
    onDetectionSelection(prediction, detection);
  }, [prediction, detection]);

  // the card's colours, by status and for the one being looked at: .dl-detection in gleamer-overrides.css
  return (
    <li
      ref={liRef}
      key={`${detection.type}-${detection.confidence}`}
      onClick={onFocus}
      onDoubleClick={onDoubleClick}
      className={classnames('dl-detection', {
        'dl-detection--active': isActive,
      })}
      data-status={status ?? 'PENDING'}
    >
      <div className="text-[13px] font-semibold tracking-[.02em]">
        {detection.type} - {index + 1} - {detection.confidence.toFixed(1)}
      </div>
      <form
        className="field-radio-group mt-2 flex gap-1.5"
        onFocus={onFocus}
        ref={formRef}
      >
        <StatusInput status="POSITIVE" detection={detection}>
          <CheckIcon className="h-4 w-4" />
        </StatusInput>
        <StatusInput status="NEGATIVE" detection={detection}>
          <XMarkIcon className="h-4 w-4" />
        </StatusInput>
        <StatusInput status="UNCERTAIN" detection={detection}>
          <div className="text-center h-4 w-4">?</div>
        </StatusInput>
      </form>
    </li>
  );
}
